import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { contractPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { formatRevSharePrice } from '@vy/services';
import { COMMITMENT_TYPES, type Currency } from '@vy/types';
import { formatNumber, isNotNullOrUndefined, isNullOrUndefined, withDefault } from '@vy/utils';
import type { FieldPath } from 'react-hook-form';
import { Price } from '../../../../../components/design/Price';
import type { VyTableFunctionalColumns } from '../../../../../components/new-design/table/vy-table/vy-table-types';
import { VY_TABLE_FUNCTIONAL_COLUMNS } from '../../../../../components/new-design/table/vy-table/vy-table-types';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { formatNumberWithPrecision } from '../../../../../utils/number';
import { getAccumulationIntervalLabel } from '../../../../plans/CreatePlanPage/shared-charge-drawer-components/components/AccumulationIntervalSelect';
import { getBillingIntervalLabel } from '../../ContractSummaryDetails/contract-summary-details-utils/get-billing-interval-label';
import type { UsageProductsColumns } from '../contract-usage-products-types/usage-products-columns';

export const USAGE_PRODUCTS_COLUMNS_KEYS = Object.freeze({
  PRODUCT_NAME: 'productName',
  PRICING_TYPE: 'pricingType',
  PRICE: 'price',
  RATE: 'rate',
  PRODUCT_ERP_ID: 'productErpId',
  PRODUCT_ANROK_ID: 'productAnrokId',
  MARKETPLACE_PRODUCT_ID: 'marketplaceProductId',
  MARKETPLACE_DIMENSION: 'marketplaceDimension',
  COMMITMENT: 'commitment',
  METER_RESET: 'meterReset',
  METER_REFILL: 'meterRefill',
  BULK: 'bulk',
  BILLING_INTERVAL: 'billingInterval',
  START_DATE: 'startDate',
} satisfies Record<string, FieldPath<UsageProductsColumns> | VyTableFunctionalColumns>);

type GetUsageProductsColumnsOptions = {
  isAnrokEnabled?: boolean;
  isConnactEnabled?: boolean;
};

export const getUsageProductsColumns = (
  currency: Currency,
  options: GetUsageProductsColumnsOptions = {},
): GridColDef<UsageProductsColumns>[] => {
  const { isAnrokEnabled = false, isConnactEnabled = false } = options;

  const baseColumns: GridColDef<UsageProductsColumns>[] = [
  {
    field: VY_TABLE_FUNCTIONAL_COLUMNS.PREFIX,
    headerName: '',
    maxWidth: 40,
    width: 40,
    sortable: false,
    hideable: false,
    renderCell: (params) =>
      params.row.isParent && (
        <IconButton size="small" onClick={() => params.row.toggleExpand?.(params.row.id)}>
          {params.row.isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      ),
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.PRODUCT_NAME,
    headerName: 'Product Name',
    flex: 1,
    minWidth: 160,
    hideable: false,
    renderCell: ({ row: { productName } }) => (
      <VyTableCell
        {...getSelectorAttribute(contractPageSelectors.usageTable.productName)}
        value={productName}
        containerClassName="justify-start"
      />
    ),
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.PRICING_TYPE,
    headerName: 'Pricing Type',
    headerAlign: 'center',
    flex: 1,
    minWidth: 160,
    hideable: false,
    renderCell: ({ row: { pricingType } }) => (
      <VyTableCell
        {...getSelectorAttribute(contractPageSelectors.usageTable.pricingType)}
        value={pricingType}
      />
    ),
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.PRICE,
    headerName: 'Price Per Unit',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    hideable: false,
    valueFormatter: value => formatNumberWithPrecision(value, 2),
    renderCell: ({ row: { price, isRevShare, isParent } }) =>
      isNotNullOrUndefined(price) && (
        <VyTableCell
          {...getSelectorAttribute(contractPageSelectors.usageTable.pricePerUnit)}
          value={formatPrice(price, currency, isRevShare, isParent)}
        />
      ),
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.RATE,
    headerName: 'Rate',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row: { rate } }) => <Price currency={currency}>{rate ?? 0}</Price>,
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.PRODUCT_ERP_ID,
    headerName: 'Product ERP ID',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    renderCell: ({ row: { productErpId } }) => <VyTableCell value={productErpId} />,
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.COMMITMENT,
    headerName: 'Commitment',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row: { commitment, unitsType, commitmentType } }) => (
      <VyTableCell
        {...getSelectorAttribute(contractPageSelectors.usageTable.commitment)}
        value={
          isNullOrUndefined(commitment)
            ? undefined
            : `${formatNumber(commitment)} ${commitmentType === COMMITMENT_TYPES.Price ? '$' : withDefault(unitsType, 'units')}`
        }
      />
    ),
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.METER_RESET,
    headerName: 'Meter Reset',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row: { meterReset } }) => (
      <VyTableCell
        {...getSelectorAttribute(contractPageSelectors.usageTable.meterReset)}
        value={isNotNullOrUndefined(meterReset) ? getAccumulationIntervalLabel(meterReset) : undefined}
      />
    ),
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.METER_REFILL,
    headerName: 'Meter Refill',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row: { meterRefill } }) => (
      <VyTableCell
        {...getSelectorAttribute(contractPageSelectors.usageTable.meterRefill)}
        value={isNotNullOrUndefined(meterRefill) ? getAccumulationIntervalLabel(meterRefill) : undefined}
      />
    ),
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.BULK,
    headerName: 'Bulk Size',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row: { bulk } }) => (
      <VyTableCell value={isNotNullOrUndefined(bulk) && bulk === 1 ? undefined : formatNumber(bulk)} />
    ),
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.BILLING_INTERVAL,
    headerName: 'Billing Interval',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row: { billingInterval } }) => (
      <VyTableCell
        {...getSelectorAttribute(contractPageSelectors.usageTable.billingInterval)}
        value={isNotNullOrUndefined(billingInterval) ? getBillingIntervalLabel(billingInterval) : undefined}
      />
    ),
  },
  {
    field: USAGE_PRODUCTS_COLUMNS_KEYS.START_DATE,
    headerName: 'Start Date',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row: { startDate } }) => <VyTableCell value={startDate} />,
  },
  ];

  const optionalColumns: GridColDef<UsageProductsColumns>[] = [];

  if (isConnactEnabled) {
    optionalColumns.push(
      {
        field: USAGE_PRODUCTS_COLUMNS_KEYS.MARKETPLACE_PRODUCT_ID,
        headerName: 'MP Product ID',
        headerClassName: 'text-sm',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 180,
      },
      {
        field: USAGE_PRODUCTS_COLUMNS_KEYS.MARKETPLACE_DIMENSION,
        headerName: 'MP Dimension',
        headerClassName: 'text-sm',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 180,
      },
    );
  }

  if (isAnrokEnabled) {
    optionalColumns.push({
      field: USAGE_PRODUCTS_COLUMNS_KEYS.PRODUCT_ANROK_ID,
      headerName: 'Anrok ID',
      headerClassName: 'text-sm',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 140,
    });
  }

  if (optionalColumns.length > 0) {
    baseColumns.splice(
      baseColumns.findIndex((col) => col.field === USAGE_PRODUCTS_COLUMNS_KEYS.PRODUCT_ERP_ID) + 1,
      0,
      ...optionalColumns,
    );
  }

  return baseColumns;
};

function formatPrice(price: number, currency: Currency, isRevShare: boolean, isParent: boolean) {
  if (isRevShare) {
    const formattedPrice = formatRevSharePrice(price);

    return isParent ? null : formattedPrice;
  }

  return <Price currency={currency}>{price}</Price>;
}
